import {useGenericQuery} from "@/hooks/useQueryHooks";
import {client} from "@/httpClient";
import {isInvalidCompanyUrlModalOpenAtom} from "@/store/jotaiAtoms";
import {useOnboardingStore} from "@/store/onboarding";
import type {TQueryOptions} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import {useSearch} from "@tanstack/react-router";
import type {AxiosResponse} from "axios";
import {useSetAtom} from "jotai";

type TViewerGeo = {
  city: string | null;
  country: string | null;
  country_name: string | null;
  time_zone: string | null;
};

export const MISC_KEYS = {
  mCrawling: ["/crawling/"],
  mGoogleLogin: ["/social_auth/google_login/"],
  mToken: ["/token/"],
  mTokenAdmin: ["/token/admin/"],
  mTokenAdminEnd: ["/token/admin/end/"],
  mTokenLogout: ["/token/logout/"],
  qCategoryList: ["/category/"],
  qCategoryListAdmin: ["/interest/"],
  qCrawlingStream: (sessionId: string) => [`/crawling/${sessionId}/stream/`],
  qGeo: ["/geo/"],
  qLocationCities: (search?: string, type?: string) => [`/locations/cities/?search=${search ?? ""}&type=${type ?? ""}`],
  qLocationRegions: (search?: string, type?: string) => [
    `/locations/regions/?search=${search ?? ""}&type=${type ?? ""}`,
  ],
  qLocationZipCodes: (search?: string) => [`/locations/zip_codes/?search=${search ?? ""}`],
  qSocialMentionsAccountTags: (search?: string, platform?: "instagram" | "tiktok") => [
    `/social_mentions/account_tags/${search || platform ? "?" : ""}${search ? `search=${search}` : ""}${search && platform ? "&" : ""}${platform ? `platform=${platform}` : ""}`,
  ],
  qSocialMentionsHashtags: (search?: string, platform?: "instagram" | "tiktok") => [
    `/social_mentions/hashtags/${search || platform ? "?" : ""}${search ? `search=${search}` : ""}${search && platform ? "&" : ""}${platform ? `platform=${platform}` : ""}`,
  ],
} as const;

export const useViewerGeoQuery = ({queryOptions}: {queryOptions?: TQueryOptions<TViewerGeo>} = {}) =>
  useGenericQuery<TViewerGeo>({
    queryKey: MISC_KEYS.qGeo,
    staleTime: Infinity,
    ...queryOptions,
  });

export const useImpersonateMutation = () =>
  useMutation<AxiosResponse<{access: string; refresh: string}>, unknown, {email: string}>({
    meta: {skipSuccessToast: true},
    mutationKey: MISC_KEYS.mTokenAdmin,
    mutationFn: ({email}) => client.post(MISC_KEYS.mTokenAdmin[0], {email}),
  });

export const useEndImpersonationMutation = () =>
  useMutation({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey: MISC_KEYS.mTokenAdminEnd,
    mutationFn: () => client.post(MISC_KEYS.mTokenAdminEnd[0]),
  });

export const useCrawlingMutation = ({
  country,
  domain,
  language,
  onError,
  onSuccess,
}: {
  country?: string;
  domain: string;
  language?: string;
  onError?: () => void;
  onSuccess?: (sessionId: string) => void;
}) =>
  useMutation<AxiosResponse<{session_id: string}>>({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey: MISC_KEYS.mCrawling,
    mutationFn: () =>
      client.post(MISC_KEYS.mCrawling[0], {
        domain,
        country,
        language,
      }),
    onSuccess: response => {
      onSuccess?.(response.data.session_id);
    },
    onError,
  });

export const useOnboardingCrawlingMutation = ({domain}: {domain: string}) => {
  const {country, language} = useSearch({from: "/_unauthenticated/onboarding"});
  const setIsInvalidCompanyUrlModalOpen = useSetAtom(isInvalidCompanyUrlModalOpenAtom);
  const setter = useOnboardingStore(state => state.setter);

  return useCrawlingMutation({
    country,
    domain,
    language,
    onError: () => {
      setIsInvalidCompanyUrlModalOpen(true);
    },
    onSuccess: sessionId => {
      setter(state => {
        state.sessionId = sessionId;
      });
    },
  });
};
